import { Capacitor } from "@capacitor/core";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  AUTH_PROVIDER_STATE,
  getAuthProviderState,
} from "../utils/authProviders";
import {
  isAppleAuthCancellation,
  isNativeIosAppleAuthAvailable,
  requestNativeAppleCredential,
} from "./appleAuth";

export const SENSITIVE_REAUTH_ERROR_CODES = Object.freeze({
  USER_MISSING: "SENSITIVE_REAUTH_USER_MISSING",
  PASSWORD_REQUIRED: "SENSITIVE_REAUTH_PASSWORD_REQUIRED",
  EMAIL_MISSING: "SENSITIVE_REAUTH_EMAIL_MISSING",
  APPLE_NATIVE_REQUIRED: "SENSITIVE_REAUTH_APPLE_NATIVE_REQUIRED",
  PROVIDER_UNSUPPORTED: "SENSITIVE_REAUTH_PROVIDER_UNSUPPORTED",
  UID_MISMATCH: "SENSITIVE_REAUTH_UID_MISMATCH",
});

function reauthError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

let appleReauthenticationInFlight = null;

export function reauthenticateWithPassword({
  firebaseUser,
  password,
  firebaseAuth = auth,
  reauthenticate = reauthenticateWithCredential,
  credentialFactory = EmailAuthProvider.credential,
} = {}) {
  if (!firebaseUser) {
    return Promise.reject(reauthError(
      SENSITIVE_REAUTH_ERROR_CODES.USER_MISSING,
      "No authenticated user is available."
    ));
  }
  if (!firebaseUser.email) {
    return Promise.reject(reauthError(
      SENSITIVE_REAUTH_ERROR_CODES.EMAIL_MISSING,
      "The authenticated account has no email address."
    ));
  }
  if (!String(password || "")) {
    return Promise.reject(reauthError(
      SENSITIVE_REAUTH_ERROR_CODES.PASSWORD_REQUIRED,
      "A password is required."
    ));
  }

  const expectedUid = String(firebaseUser.uid || "");
  if (!expectedUid || String(firebaseAuth.currentUser?.uid || "") !== expectedUid) {
    return Promise.reject(reauthError(
      SENSITIVE_REAUTH_ERROR_CODES.USER_MISSING,
      "The current Firebase session does not match the requested user."
    ));
  }

  const credential = credentialFactory(firebaseUser.email, String(password));
  return reauthenticate(firebaseUser, credential).then((result) => {
    const resultUid = String(result?.user?.uid || firebaseUser.uid || "");
    if (
      resultUid !== expectedUid ||
      String(firebaseAuth.currentUser?.uid || "") !== expectedUid
    ) {
      throw reauthError(
        SENSITIVE_REAUTH_ERROR_CODES.UID_MISMATCH,
        "Password confirmation returned a different Firebase account."
      );
    }
    return { method: "password", cancelled: false, uid: expectedUid };
  });
}

export function reauthenticateWithApple({
  firebaseUser,
  firebaseAuth = auth,
  capacitor = Capacitor,
  requestAppleCredential = requestNativeAppleCredential,
  reauthenticate = reauthenticateWithCredential,
} = {}) {
  if (appleReauthenticationInFlight) return appleReauthenticationInFlight;

  appleReauthenticationInFlight = (async () => {
    const expectedUid = String(firebaseUser?.uid || "");
    if (!expectedUid || String(firebaseAuth.currentUser?.uid || "") !== expectedUid) {
      throw reauthError(
        SENSITIVE_REAUTH_ERROR_CODES.USER_MISSING,
        "The current Firebase session does not match the requested user."
      );
    }
    if (!isNativeIosAppleAuthAvailable(capacitor)) {
      throw reauthError(
        SENSITIVE_REAUTH_ERROR_CODES.APPLE_NATIVE_REQUIRED,
        "Apple confirmation is only available in the VaultQuest iOS app."
      );
    }

    try {
      const { credential, appleResult } = await requestAppleCredential();
      const result = await reauthenticate(firebaseUser, credential);
      const resultUid = String(result?.user?.uid || firebaseUser.uid || "");
      const currentUid = String(firebaseAuth.currentUser?.uid || "");
      if (resultUid !== expectedUid || currentUid !== expectedUid) {
        throw reauthError(
          SENSITIVE_REAUTH_ERROR_CODES.UID_MISMATCH,
          "Apple confirmation returned a different Firebase account."
        );
      }
      return {
        method: "apple",
        cancelled: false,
        uid: expectedUid,
        authorizationCode: String(appleResult?.authorizationCode || "").trim(),
      };
    } catch (error) {
      if (isAppleAuthCancellation(error)) {
        return { method: "apple", cancelled: true, uid: expectedUid };
      }
      throw error;
    }
  })().finally(() => {
    appleReauthenticationInFlight = null;
  });

  return appleReauthenticationInFlight;
}

export function reauthenticateForSensitiveAction({
  firebaseUser = auth.currentUser,
  password = "",
  capacitor = Capacitor,
  ...dependencies
} = {}) {
  const providerState = getAuthProviderState(firebaseUser);
  const nativeAppleAvailable = isNativeIosAppleAuthAvailable(capacitor);

  if (
    providerState === AUTH_PROVIDER_STATE.APPLE_ONLY ||
    (providerState === AUTH_PROVIDER_STATE.PASSWORD_AND_APPLE && nativeAppleAvailable)
  ) {
    return reauthenticateWithApple({
      firebaseUser,
      capacitor,
      ...dependencies,
    });
  }

  if (
    providerState === AUTH_PROVIDER_STATE.PASSWORD_ONLY ||
    providerState === AUTH_PROVIDER_STATE.PASSWORD_AND_APPLE
  ) {
    return reauthenticateWithPassword({
      firebaseUser,
      password,
      ...dependencies,
    });
  }

  return Promise.reject(reauthError(
    SENSITIVE_REAUTH_ERROR_CODES.PROVIDER_UNSUPPORTED,
    "The account has no supported authentication provider."
  ));
}
